import React, { useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { toast } from "react-toastify";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { palette } from "./theme";

export const DOC_REQUIREMENTS_KEY = "autoRunDocumentRequirements";

// Must match REQUIREMENT_IDS in functions/utils/autoRunDocumentGate.js. The
// backend owns what each requirement means; this is only how it is labelled.
export const DOC_REQUIREMENTS = [
  {
    id: "credit",
    label: "Credit",
    task: "Credit",
    help: "The Credit task reads tradeline data, so it needs a report that carries some.",
  },
  {
    id: "income",
    label: "Income",
    task: "Income/Employment",
    help: "The Income task needs a document stating an income figure, not just proof a business exists.",
  },
  {
    id: "asset",
    label: "Assets",
    task: "Asset",
    help: "The Asset task needs a statement showing a balance, not a letter promising one.",
  },
];

/** Largest minimum the backend will accept — MAX_MINIMUM_DOCUMENT_COUNT. */
export const MAX_MINIMUM_COUNT = 10;

const sx = {
  panel: {
    background: palette.surface,
    border: `1px solid ${palette.border}`,
    borderRadius: 16,
    padding: "22px 24px",
    boxShadow: palette.shadow,
    marginBottom: 20,
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 18,
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: palette.textDim,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: palette.accent,
    display: "inline-block",
  },
  title: {
    margin: "8px 0 4px",
    fontSize: 18,
    fontWeight: 700,
    color: palette.text,
  },
  help: { margin: 0, fontSize: 13, color: palette.textMuted, maxWidth: 720 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 16,
  },
  card: (isOn) => ({
    border: `1px solid ${isOn ? palette.borderStrong : palette.border}`,
    borderRadius: 14,
    padding: 16,
    background: isOn ? palette.surfaceTint : palette.trackSoft,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  }),
  cardTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  cardLabel: { fontSize: 15, fontWeight: 700, color: palette.text },
  cardTask: { fontSize: 12, color: palette.textDim, marginTop: 2 },
  cardHelp: { fontSize: 12, color: palette.textMuted, lineHeight: 1.5 },
  switch: (isOn) => ({
    width: 42,
    height: 24,
    borderRadius: 999,
    border: "none",
    cursor: "pointer",
    padding: 2,
    flexShrink: 0,
    background: isOn ? palette.accent : palette.idle,
    transition: "background 120ms ease",
  }),
  knob: (isOn) => ({
    width: 20,
    height: 20,
    borderRadius: "50%",
    background: "#fff",
    transform: `translateX(${isOn ? 18 : 0}px)`,
    transition: "transform 120ms ease",
  }),
  countRow: { display: "flex", alignItems: "center", gap: 10 },
  countInput: {
    width: 64,
    padding: "7px 10px",
    borderRadius: 9,
    border: `1px solid ${palette.borderStrong}`,
    fontSize: 14,
    color: palette.text,
    background: palette.surface,
  },
  countLabel: { fontSize: 12, color: palette.textMuted },
  searchWrap: { position: "relative" },
  searchIcon: {
    position: "absolute",
    left: 10,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 17,
    color: palette.textDim,
  },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 10px 8px 32px",
    borderRadius: 9,
    border: `1px solid ${palette.border}`,
    fontSize: 13,
    color: palette.text,
    background: palette.surface,
  },
  chips: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: (isUnknown) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "4px 6px 4px 9px",
    borderRadius: 999,
    fontSize: 12,
    color: isUnknown ? palette.warn : palette.text,
    background: isUnknown ? palette.warnSoft : palette.track,
    border: `1px solid ${isUnknown ? palette.warn : "transparent"}`,
  }),
  chipRemove: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    display: "inline-flex",
    padding: 0,
    color: "inherit",
  },
  options: {
    maxHeight: 200,
    overflowY: "auto",
    border: `1px solid ${palette.border}`,
    borderRadius: 9,
    background: palette.surface,
  },
  optionGroup: {
    padding: "6px 10px",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: palette.textDim,
    background: palette.trackSoft,
    position: "sticky",
    top: 0,
  },
  option: (isSelected) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    textAlign: "left",
    padding: "7px 10px",
    fontSize: 13,
    border: "none",
    cursor: "pointer",
    color: isSelected ? palette.accent : palette.text,
    background: isSelected ? palette.accentSoft : "transparent",
  }),
  addFreeText: {
    width: "100%",
    textAlign: "left",
    padding: "8px 10px",
    fontSize: 13,
    border: "none",
    borderTop: `1px solid ${palette.border}`,
    cursor: "pointer",
    color: palette.accent,
    background: palette.surface,
  },
  warning: {
    fontSize: 12,
    color: palette.warn,
    display: "flex",
    gap: 6,
    lineHeight: 1.5,
  },
  error: { fontSize: 12, color: palette.danger, lineHeight: 1.5 },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
    marginTop: 18,
  },
  saveButton: (canSave) => ({
    padding: "9px 18px",
    borderRadius: 10,
    border: "none",
    fontSize: 13,
    fontWeight: 700,
    cursor: canSave ? "pointer" : "not-allowed",
    color: "#fff",
    background: canSave ? palette.accent : palette.idle,
  }),
  resetButton: {
    padding: "9px 14px",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    color: palette.textMuted,
    background: "transparent",
    border: `1px solid ${palette.border}`,
  },
};

const sameNames = (a, b) =>
  a.length === b.length && a.every((name, index) => name === b[index]);

/**
 * Searchable multi-select over the classification taxonomy.
 *
 * Free text is allowed because classification can add a type before this list
 * catches up, but anything the taxonomy does not recognize is flagged rather
 * than accepted quietly — an unmatched name silently satisfies nothing.
 */
function DocumentTypePicker({ categories, selected, knownNames, onChange }) {
  const [query, setQuery] = useState("");

  const selectedKeys = useMemo(
    () => new Set(selected.map((name) => name.toLowerCase())),
    [selected],
  );

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return categories;
    }

    return categories
      .map((group) => ({
        ...group,
        documentTypes: group.documentTypes.filter((name) =>
          name.toLowerCase().includes(needle),
        ),
      }))
      .filter((group) => group.documentTypes.length > 0);
  }, [categories, query]);

  const trimmedQuery = query.trim();
  const canAddFreeText =
    trimmedQuery.length > 0 &&
    !knownNames.has(trimmedQuery.toLowerCase()) &&
    !selectedKeys.has(trimmedQuery.toLowerCase());

  const toggle = (name) => {
    const key = name.toLowerCase();
    onChange(
      selectedKeys.has(key)
        ? selected.filter((current) => current.toLowerCase() !== key)
        : [...selected, name],
    );
  };

  return (
    <>
      <div style={sx.chips}>
        {selected.length === 0 && (
          <span style={sx.error}>
            Pick at least one document — a requirement nothing satisfies blocks
            its task on every loan.
          </span>
        )}
        {selected.map((name) => {
          const isUnknown = !knownNames.has(name.toLowerCase());

          return (
            <span key={name} style={sx.chip(isUnknown)}>
              {name}
              <button
                type="button"
                aria-label={`Remove ${name}`}
                style={sx.chipRemove}
                onClick={() => toggle(name)}
              >
                <CloseRoundedIcon style={{ fontSize: 14 }} />
              </button>
            </span>
          );
        })}
      </div>

      <div style={sx.searchWrap}>
        <SearchRoundedIcon style={sx.searchIcon} />
        <input
          type="text"
          value={query}
          placeholder="Search document types"
          onChange={(event) => setQuery(event.target.value)}
          style={sx.searchInput}
        />
      </div>

      <div style={sx.options}>
        {matches.map((group) => (
          <div key={group.category}>
            <div style={sx.optionGroup}>{group.category}</div>
            {group.documentTypes.map((name) => {
              const isSelected = selectedKeys.has(name.toLowerCase());

              return (
                <button
                  key={name}
                  type="button"
                  style={sx.option(isSelected)}
                  onClick={() => toggle(name)}
                >
                  <input type="checkbox" readOnly checked={isSelected} />
                  {name}
                </button>
              );
            })}
          </div>
        ))}
        {canAddFreeText && (
          <button
            type="button"
            style={sx.addFreeText}
            onClick={() => {
              toggle(trimmedQuery);
              setQuery("");
            }}
          >
            Add "{trimmedQuery}" — not in the classification list
          </button>
        )}
        {matches.length === 0 && !canAddFreeText && (
          <div style={{ padding: 10, fontSize: 13, color: palette.textDim }}>
            No document types match.
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Global editor for the Borrower Approval minimum-document gate.
 *
 * These settings apply to every organization. An organization can switch a
 * requirement off or raise its minimum from its own dialog, but the document
 * lists are only editable here — which documents answer a Credit question is a
 * property of the taxonomy, not of a tenant.
 */
function MinimumDocumentsPanel({ env, baseUrls, requirements, onSaved }) {
  const [catalog, setCatalog] = useState(null);
  const [draft, setDraft] = useState(requirements || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(requirements || {});
  }, [requirements]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const userId = getAuth()?.currentUser?.uid;
      if (!userId || !baseUrls?.length) {
        return;
      }

      for (const baseUrl of baseUrls) {
        try {
          const resp = await fetch(
            `${baseUrl}/admin/document-type-catalog?userId=${encodeURIComponent(userId)}`,
          );
          const json = await resp.json();
          if (resp.ok && json.success && !cancelled) {
            setCatalog(json);

            return;
          }
        } catch (_err) {
          // Try the next base URL before giving up.
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [baseUrls]);

  const knownNames = useMemo(
    () =>
      new Set(
        (catalog?.categories || []).flatMap((group) =>
          group.documentTypes.map((name) => name.toLowerCase()),
        ),
      ),
    [catalog],
  );

  const update = (id, patch) =>
    setDraft((current) => ({
      ...current,
      [id]: { ...current[id], ...patch },
    }));

  const isDirty = DOC_REQUIREMENTS.some(({ id }) => {
    const next = draft[id] || {};
    const saved = requirements?.[id] || {};

    return (
      next.enabled !== saved.enabled ||
      next.minimumCount !== saved.minimumCount ||
      !sameNames(next.documentTypes || [], saved.documentTypes || [])
    );
  });

  // An enabled requirement with no documents can never be met, and a minimum
  // above the list length is the same trap in slower motion.
  const problem = DOC_REQUIREMENTS.map(({ id, label }) => {
    const entry = draft[id] || {};
    const count = (entry.documentTypes || []).length;
    if (count === 0) {
      return `${label} has no document types selected.`;
    }
    if (entry.enabled && entry.minimumCount > count) {
      return `${label} asks for ${entry.minimumCount} documents but only ${count} type${count === 1 ? "" : "s"} can satisfy it.`;
    }

    return null;
  }).find(Boolean);

  const canSave = isDirty && !problem && !saving;

  const handleSave = async () => {
    setSaving(true);
    try {
      const userId = getAuth()?.currentUser?.uid;
      if (!userId) {
        toast.error("Admin user not found. Please refresh and sign in again.");

        return;
      }

      const payload = Object.fromEntries(
        DOC_REQUIREMENTS.map(({ id }) => [
          id,
          {
            enabled: !!draft[id]?.enabled,
            minimumCount: draft[id]?.minimumCount || 1,
            documentTypes: draft[id]?.documentTypes || [],
          },
        ]),
      );

      let lastError = "Failed to save minimum documents";
      for (const baseUrl of baseUrls) {
        try {
          const resp = await fetch(
            `${baseUrl}/admin/organizations/usage-limits/defaults`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                userId,
                globalDefaults: { [DOC_REQUIREMENTS_KEY]: payload },
              }),
            },
          );
          const json = await resp.json();
          if (resp.ok && json.success) {
            toast.success("Minimum documents saved");
            onSaved?.(json.globalDefaults);

            return;
          }
          lastError = json.reason || lastError;
        } catch (err) {
          lastError = err.message || lastError;
        }
      }
      toast.error(lastError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={sx.panel}>
      <div style={sx.header}>
        <div>
          <div style={sx.eyebrow}>
            <span style={sx.dot} />
            System defaults
          </div>
          <h2 style={sx.title}>Minimum documents for Borrower Approval</h2>
          <p style={sx.help}>
            An automatic run skips a task unless the loan is already carrying
            the documents it reads, so it doesn&apos;t spend one of the
            loan&apos;s few lifetime runs answering with nothing. These lists
            apply to every org; an org can switch a requirement off or raise its
            minimum from its own dialog. Manual runs are never gated, and
            neither is NewFi.
          </p>
        </div>
        <span style={sx.eyebrow}>{env}</span>
      </div>

      <div style={sx.grid}>
        {DOC_REQUIREMENTS.map(({ id, label, task, help }) => {
          const entry = draft[id] || {};
          const isOn = !!entry.enabled;
          const selected = entry.documentTypes || [];

          return (
            <div key={id} style={sx.card(isOn)}>
              <div style={sx.cardTop}>
                <div>
                  <div style={sx.cardLabel}>{label}</div>
                  <div style={sx.cardTask}>Gates the “{task}” task</div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isOn}
                  aria-label={`${label} minimum documents`}
                  style={sx.switch(isOn)}
                  onClick={() => update(id, { enabled: !isOn })}
                >
                  <span style={sx.knob(isOn)} />
                </button>
              </div>

              <div style={sx.cardHelp}>{help}</div>

              <div style={sx.countRow}>
                <input
                  type="number"
                  min="1"
                  max={MAX_MINIMUM_COUNT}
                  value={entry.minimumCount ?? 1}
                  disabled={!isOn}
                  onChange={(event) =>
                    update(id, {
                      minimumCount: Math.min(
                        MAX_MINIMUM_COUNT,
                        Math.max(1, Number(event.target.value) || 1),
                      ),
                    })
                  }
                  style={sx.countInput}
                />
                <span style={sx.countLabel}>
                  qualifying document
                  {(entry.minimumCount ?? 1) === 1 ? "" : "s"} required
                </span>
              </div>

              {catalog ? (
                <DocumentTypePicker
                  categories={catalog.categories}
                  selected={selected}
                  knownNames={knownNames}
                  onChange={(documentTypes) => update(id, { documentTypes })}
                />
              ) : (
                <div style={{ fontSize: 13, color: palette.textDim }}>
                  Loading document types…
                </div>
              )}

              {selected.some((name) => !knownNames.has(name.toLowerCase())) &&
                catalog && (
                  <div style={sx.warning}>
                    Highlighted names are not in the classification list, so no
                    document will ever match them. Remove them unless
                    classification is about to start using them.
                  </div>
                )}
            </div>
          );
        })}
      </div>

      <div style={sx.footer}>
        {problem && <span style={sx.error}>{problem}</span>}
        {isDirty && (
          <button
            type="button"
            style={sx.resetButton}
            onClick={() => setDraft(requirements || {})}
          >
            Reset
          </button>
        )}
        <button
          type="button"
          disabled={!canSave}
          style={sx.saveButton(canSave)}
          onClick={handleSave}
        >
          {saving ? "Saving…" : "Save minimum documents"}
        </button>
      </div>
    </section>
  );
}

export default MinimumDocumentsPanel;
